import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, makeStyles, Theme } from "@material-ui/core";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import { ActionsColumn, DataGrid, DollarColumn, GridColumnProps, StandardDialog, TextColumn } from "@roc/ui";
import { Delete, Edit } from "@material-ui/icons";
import { useDocumentStore } from "@roc/feature-documents";
import { AddServicerForm } from "./addServicerForm";
import { Document } from "@roc/feature-types";
import { CustomTaskDialog } from "@roc/feature-general-tasks";

const useStyles = makeStyles((theme: Theme) => ({
  button: {
    marginBottom: '1em',
    minWidth: 'fit-content'
  },
  radioField: {
    paddingLeft: '10px',
  },
  table: {
    minWidth: 750,
    border: '2px #e0e0e0 solid',
    '& td': {
      height: '40px',
      padding: 0,
      paddingTop: '8px',
      paddingBottom: '8px',
    },
    '& th:first-child, td[rowSpan], $addNewItemRow td': {
      paddingLeft: theme.spacing(2),
      paddingRight: '3px',
    },
    '& table tr:last-child td, & table td[rowSpan]': {
      borderBottom: 'none',
    },
  },
}));

interface Props {
  open: boolean;
  loan: any;
  onClose: () => void;
  document: Document;
}

const feeHeaders: GridColumnProps[] = [
  {
    field: 'description',
    headerName: 'Category',
    maxWidth: 500,
    cellRenderer: 'textCellRenderer',
  },
  {
    field: 'origAmt',
    headerName: 'Amount',
    minWidth: 100,
    cellRenderer: 'moneyCellRenderer',
  },
  {
    field: 'actions',
    headerName: 'Actions',
    maxWidth: 200,
    cellRenderer: 'actionsCellRenderer',
  },
];

export const ServicerFees = observer(({ open, loan, onClose, document }: Props) => {
  const classes = useStyles();
  const {
    documentStore: {
      documentFormStore: { servicerFeeStore },
    },
  } = useDocumentStore();

  const [addFeeOpen, setAddFeeOpen] = useState(false);
  const [isAddMode, setIsAddMode] = useState(false);
  const [servicerFeesList, setServicerFeesList] = useState([]);
  const taskId = document?.taskId;

  useEffect(() => {
    if (open) {
      servicerFeeStore.resetStore();
      const loadServicerFees = async () => {
        await servicerFeeStore.getServicerFees(loan);
      };
      loadServicerFees();
      setServicerFeesList(servicerFeeStore.getFeeList());
      servicerFeeStore.setLoanId(loan.loanId);
    }
  }, [open]);

  useEffect(() => {
    setServicerFeesList(servicerFeeStore.getFeeList());
  }, [servicerFeeStore.servicerFees]);

  const onSubmit = () => {
    servicerFeeStore.saveServicerFees(servicerFeesList);
    onClose();
  }

  const frameworkComponents = {
    textCellRenderer: params => {
      const desc = params?.node?.data?.description;
      return desc || '';
    },
    moneyCellRenderer: params => {
      const amount = params?.node?.data?.origAmt;
      return DollarColumn(amount);
    },
    actionsCellRenderer: params =>
      ActionsColumn({
        ...params,
        actions: getActions(params),
      }),
  };

  const getActions = params => [
    {
      icon: <Delete color="primary" />,
      onClick: () => handleDeleteFee(params.rowIndex),
    },
    {
      icon: <Edit color="primary" />,
      onClick: () => handleEditFee(params.node.data, params.rowIndex),
    }
  ];

  const handleDeleteFee = async index => {
    servicerFeeStore.deleteFee(index);
    setServicerFeesList(servicerFeeStore.getFeeList());
  };

  const handleEditFee = async (fee, index) => {
    servicerFeeStore.setCurrentSelectedFee(fee);
    servicerFeeStore.setCurrentSelectedIndex(index);
    setAddFeeOpen(true);
    setIsAddMode(false);
  }

  const handleCloseAddFeeDialog = () => {
    setServicerFeesList(servicerFeeStore.getFeeList());
    setAddFeeOpen(false);
    servicerFeeStore.setCurrentSelectedIndex(null);
    servicerFeeStore.setCurrentSelectedFee(null);
  }

  const getContent = () => {
    return (
      <>
        <Grid container>
          <Grid container spacing={1} justifyContent="flex-end">
            <Grid item xs={2} className={classes.button}>
              <Button
                onClick={() => {
                  setAddFeeOpen(true);
                  setIsAddMode(true);
                }}
                color="primary"
                variant="outlined"
              >
                + Add New Fee
              </Button>
            </Grid>
          </Grid>
          <DataGrid
            columns={feeHeaders}
            rows={servicerFeeStore.servicerFees}
            domLayout="autoHeight"
            frameworkComponents={frameworkComponents}
          />
        </Grid>
      </>
    );
  }

  const getDialogActions = () => {
    return (
      <Grid container spacing={1} justifyContent="flex-end">
        <Grid item>
          <Button
            onClick={onClose}
            color="primary"
            variant="outlined"
          >
            Cancel
          </Button>
        </Grid>
        <Grid item>
          <Button
            onClick={onSubmit}
            color="primary"
            variant="contained"
            disabled={false}
          >
            Save
          </Button>
        </Grid>
      </Grid>
    );
  }

  return (
    <div>
      {taskId ? (
        <CustomTaskDialog
          open={open}
          title="Servicer Fees"
          maxWidth="md"
          handleClose={onClose}
          dialogContent={getContent()}
          dialogActions={getDialogActions()}
          taskId={taskId}
        />
      ) : (
        <StandardDialog
          open={open}
          title="Servicer Fees"
          maxWidth="md"
          handleClose={onClose}
          dialogContent={getContent()}
          dialogActions={getDialogActions()}
        />
      )}
      <Dialog
        disableEscapeKeyDown
        open={addFeeOpen}
        onClose={handleCloseAddFeeDialog}
        aria-labelledby="form-dialog-title"
        maxWidth="md"
      >
        <DialogTitle
          id="form-dialog-title"
        >
          {isAddMode ? 'Add Borrower Entity' : 'Edit Borrower Entity'}
        </DialogTitle>
        <DialogContent>
          <Box m={4}>
            <AddServicerForm store={servicerFeeStore} />
          </Box>
        </DialogContent>
        <DialogActions disableSpacing>
          <Button onClick={handleCloseAddFeeDialog} color="primary">
            Cancel
          </Button>
          <Button
            disabled={false}
            onClick={e => {
              const success = isAddMode ? servicerFeeStore.addFee() : servicerFeeStore.editFee();
              if (success) handleCloseAddFeeDialog();
            }}
            color="primary"
            variant="contained"
          >
            {'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
});
